import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns'
import { sk } from 'date-fns/locale'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

interface DatePickerProps {
  value?: string
  onChange?: (value: string | undefined) => void
  /** ISO date (yyyy-MM-dd); days after this are disabled. */
  max?: string
  /** ISO date (yyyy-MM-dd); days before this are disabled. */
  min?: string
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  invalid?: boolean
  /**
   * 'month' picks a whole month: the panel opens on the month grid and the
   * value is always the 1st of the chosen month (yyyy-MM-01).
   */
  picker?: 'date' | 'month'
  className?: string
}

type Mode = 'days' | 'months' | 'years'

const WEEKDAYS = ['po', 'ut', 'st', 'št', 'pi', 'so', 'ne']
const MONTH_NAMES = Array.from({ length: 12 }, (_, m) =>
  format(new Date(2000, m, 1), 'LLLL', { locale: sk })
)
const PANEL_WIDTH = 280
const PANEL_HEIGHT = 330
const YEAR_BLOCK = 12

function toDate(iso?: string) {
  if (!iso) return undefined
  const d = parseISO(iso)
  return isNaN(d.getTime()) ? undefined : d
}

export function DatePicker({
  value,
  onChange,
  max,
  min,
  placeholder = 'Vyberte dátum',
  disabled,
  clearable,
  invalid,
  picker = 'date',
  className
}: DatePickerProps) {
  const monthPicker = picker === 'month'
  const [open, setOpen] = React.useState(false)
  const [mode, setMode] = React.useState<Mode>('days')
  const [coords, setCoords] = React.useState({ top: 0, left: 0 })
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)

  const selected = toDate(value)
  const maxDate = toDate(max)
  const minDate = toDate(min)

  // With nothing selected, open on the latest pickable month instead of a
  // fully disabled current month (e.g. a birth-date field capped 18y back).
  const initialView = () =>
    selected ??
    (maxDate && isBefore(maxDate, new Date()) ? maxDate : new Date())

  const [viewMonth, setViewMonth] = React.useState<Date>(initialView)

  const reposition = React.useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const openUp =
      r.bottom + PANEL_HEIGHT > window.innerHeight && r.top > PANEL_HEIGHT
    const top = openUp ? r.top - PANEL_HEIGHT - 6 : r.bottom + 6
    const left = Math.max(
      8,
      Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8)
    )
    setCoords({ top, left })
  }, [])

  React.useEffect(() => {
    if (!open) return
    setViewMonth(initialView())
    setMode(monthPicker ? 'months' : 'days')
    reposition()
    const onScroll = () => reposition()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const year = viewMonth.getFullYear()
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 })
  })
  const blockStart = Math.floor(year / YEAR_BLOCK) * YEAR_BLOCK
  const years = Array.from({ length: YEAR_BLOCK }, (_, i) => blockStart + i)

  const maxY = maxDate?.getFullYear()
  const maxM = maxDate?.getMonth()
  const minY = minDate?.getFullYear()
  const minM = minDate?.getMonth()

  const dayOff = (d: Date) =>
    (maxDate && isAfter(startOfDay(d), startOfDay(maxDate))) ||
    (minDate && isBefore(startOfDay(d), startOfDay(minDate)))
  const monthOff = (m: number) =>
    (maxY != null && (year > maxY || (year === maxY && m > maxM!))) ||
    (minY != null && (year < minY || (year === minY && m < minM!)))
  const yearOff = (y: number) =>
    (maxY != null && y > maxY) || (minY != null && y < minY)

  const headerLabel =
    mode === 'days'
      ? format(viewMonth, 'LLLL yyyy', { locale: sk })
      : mode === 'months'
        ? String(year)
        : `${blockStart} – ${blockStart + YEAR_BLOCK - 1}`

  const step = (dir: 1 | -1) =>
    setViewMonth((m) =>
      mode === 'days'
        ? dir === 1
          ? addMonths(m, 1)
          : subMonths(m, 1)
        : mode === 'months'
          ? new Date(m.getFullYear() + dir, m.getMonth(), 1)
          : new Date(m.getFullYear() + dir * YEAR_BLOCK, m.getMonth(), 1)
    )

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-2xl border bg-white px-3.5 text-left text-sm text-ink transition-colors hover:border-ink/30 focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-destructive' : 'border-ink/[0.16]'
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected
            ? format(selected, monthPicker ? 'LLLL yyyy' : 'd. M. yyyy', {
                locale: sk
              })
            : placeholder}
        </span>
        {clearable && selected && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="clear"
            onClick={(e) => {
              e.stopPropagation()
              onChange?.(undefined)
            }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-ink/[0.06] hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : (
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-floating-panel=""
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: PANEL_WIDTH
            }}
            className="pointer-events-auto z-[1300] overflow-hidden rounded-2xl border border-ink/[0.08] bg-white p-3 shadow-[0_16px_40px_rgba(4,33,37,0.16)]"
          >
            <div className="mb-2 flex items-center justify-between gap-1">
              <button
                type="button"
                onClick={() => step(-1)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/60 transition-colors hover:bg-ink/[0.06] hover:text-ink"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setMode((m) =>
                    m === 'days'
                      ? 'months'
                      : m === 'months'
                        ? 'years'
                        : monthPicker
                          ? 'months'
                          : 'days'
                  )
                }
                className="flex-1 rounded-xl border-0 bg-transparent px-2 py-1 font-display text-sm font-bold capitalize text-ink transition-colors hover:bg-ink/[0.05]"
              >
                {headerLabel}
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/60 transition-colors hover:bg-ink/[0.06] hover:text-ink"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {mode === 'days' && (
              <>
                <div className="mb-1 grid grid-cols-7 gap-0.5">
                  {WEEKDAYS.map((w) => (
                    <span
                      key={w}
                      className="flex h-7 items-center justify-center text-xs font-semibold text-ink/40"
                    >
                      {w}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {days.map((d) => {
                    const outside = !isSameMonth(d, viewMonth)
                    const isSelected = selected && isSameDay(d, selected)
                    const isToday = isSameDay(d, new Date())
                    const off = dayOff(d)
                    return (
                      <button
                        key={d.toISOString()}
                        type="button"
                        disabled={off}
                        onClick={() => {
                          onChange?.(format(d, 'yyyy-MM-dd'))
                          setOpen(false)
                        }}
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-sm transition-colors',
                          isSelected
                            ? 'bg-primary font-semibold text-white hover:bg-primary'
                            : off
                              ? 'cursor-not-allowed text-ink/20'
                              : cn(
                                  'hover:bg-primary/10',
                                  outside ? 'text-ink/30' : 'text-ink',
                                  isToday && 'font-bold text-primary'
                                )
                        )}
                      >
                        {format(d, 'd')}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {mode === 'months' && (
              <div className="grid grid-cols-3 gap-1.5">
                {MONTH_NAMES.map((name, m) => {
                  const isSelected =
                    selected &&
                    selected.getFullYear() === year &&
                    selected.getMonth() === m
                  const off = monthOff(m)
                  return (
                    <button
                      key={name}
                      type="button"
                      disabled={off}
                      onClick={() => {
                        if (monthPicker) {
                          onChange?.(format(new Date(year, m, 1), 'yyyy-MM-dd'))
                          setOpen(false)
                          return
                        }
                        setViewMonth(new Date(year, m, 1))
                        setMode('days')
                      }}
                      className={cn(
                        'flex h-10 items-center justify-center rounded-xl border-0 bg-transparent p-0 text-sm capitalize transition-colors',
                        isSelected
                          ? 'bg-primary font-semibold text-white hover:bg-primary'
                          : off
                            ? 'cursor-not-allowed text-ink/20'
                            : 'text-ink hover:bg-primary/10'
                      )}
                    >
                      {name}
                    </button>
                  )
                })}
              </div>
            )}

            {mode === 'years' && (
              <div className="grid grid-cols-3 gap-1.5">
                {years.map((y) => {
                  const isSelected = selected && selected.getFullYear() === y
                  const off = yearOff(y)
                  return (
                    <button
                      key={y}
                      type="button"
                      disabled={off}
                      onClick={() => {
                        setViewMonth(new Date(y, viewMonth.getMonth(), 1))
                        setMode('months')
                      }}
                      className={cn(
                        'flex h-10 items-center justify-center rounded-xl border-0 bg-transparent p-0 text-sm transition-colors',
                        isSelected
                          ? 'bg-primary font-semibold text-white hover:bg-primary'
                          : off
                            ? 'cursor-not-allowed text-ink/20'
                            : 'text-ink hover:bg-primary/10'
                      )}
                    >
                      {y}
                    </button>
                  )
                })}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
